import { useMemo } from 'react'
import type { MessageDescriptor } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import useEnabledLuxPlusBenefits from 'luxPlus/hooks/useEnabledLuxPlusBenefits'
import { LANDING_BENEFIT_MESSAGES } from 'luxPlus/constants/benefits'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

export type FrontendOnlyBenefitType = 'exclusive_partner_offers' | 'weekly_member_discounts'
export type LandingBenefitType = App.LuxPlusBenefitType | FrontendOnlyBenefitType

interface ApiBenefitEntry {
  type: App.LuxPlusBenefitType
  title: MessageDescriptor
  description?: MessageDescriptor
  frontendOnly?: false
}

interface FrontendOnlyBenefitEntry {
  type: FrontendOnlyBenefitType
  title: MessageDescriptor
  description?: MessageDescriptor
  frontendOnly: true
}

export type LandingBenefitEntry = ApiBenefitEntry | FrontendOnlyBenefitEntry

const orderedLandingBenefits: Array<LandingBenefitType> = [
  'discounted_flash',
  'discounted_experiences',
  'hotel_inclusions',
  'exclusive_offers',
  'early_access',
  'discounted_tours',
  'exclusive_partner_offers',
  'cruise_inclusions',
  'weekly_member_discounts',
  'priority_support',
]

const LANDING_BENEFIT_CONTENT: Partial<Record<LandingBenefitType, LandingBenefitEntry>> = {
  discounted_flash: { type: 'discounted_flash', title: LANDING_BENEFIT_MESSAGES.discountedFlashTitle, description: LANDING_BENEFIT_MESSAGES.discountedFlashDescription },
  discounted_experiences: { type: 'discounted_experiences', title: LANDING_BENEFIT_MESSAGES.discountedExperiencesTitle, description: LANDING_BENEFIT_MESSAGES.discountedExperiencesDescription },
  hotel_inclusions: { type: 'hotel_inclusions', title: LANDING_BENEFIT_MESSAGES.hotelInclusionsTitle, description: LANDING_BENEFIT_MESSAGES.hotelInclusionsDescription },
  exclusive_offers: { type: 'exclusive_offers', title: LANDING_BENEFIT_MESSAGES.exclusiveOffersTitle },
  early_access: { type: 'early_access', title: LANDING_BENEFIT_MESSAGES.earlyAccessTitle },
  discounted_tours: { type: 'discounted_tours', title: LANDING_BENEFIT_MESSAGES.discountedToursTitle },
  cruise_inclusions: { type: 'cruise_inclusions', title: LANDING_BENEFIT_MESSAGES.cruiseInclusionsTitle },
  priority_support: { type: 'priority_support', title: LANDING_BENEFIT_MESSAGES.prioritySupportTitle, description: LANDING_BENEFIT_MESSAGES.prioritySupportDescription },
  exclusive_partner_offers: { type: 'exclusive_partner_offers', title: LANDING_BENEFIT_MESSAGES.partnerOffersTitle, description: LANDING_BENEFIT_MESSAGES.partnerOffersDescription, frontendOnly: true },
  weekly_member_discounts: { type: 'weekly_member_discounts', title: LANDING_BENEFIT_MESSAGES.weeklyDiscountsTitle, frontendOnly: true },
}

function useLandingBenefits(): Array<LandingBenefitEntry> {
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)
  const offer = useMemo(() =>
    subscriptionProduct.offers?.find(o => !o.joinFeeWaived),
  [subscriptionProduct.offers])

  const enabledBenefits = useEnabledLuxPlusBenefits(offer?.benefits)

  return useMemo(() => {
    const enabledBenefitTypes = new Set<string>(enabledBenefits.map(b => b.type))

    const regionEnabledBenefits = orderedLandingBenefits.reduce<Array<LandingBenefitEntry>>((acc, type) => {
      const entry = LANDING_BENEFIT_CONTENT[type]
      if (!entry) return acc
      if (!entry.frontendOnly && !enabledBenefitTypes.has(entry.type)) return acc

      if (type === 'priority_support' && !canRedeemLuxPlusBenefits) {
        const { description, ...rest } = entry
        acc.push(rest)
      } else if (type === 'exclusive_partner_offers' && !enabledBenefitTypes.has('partner_promo_code')) {
        // exclude
      } else {
        acc.push(entry)
      }

      return acc
    }, [])

    return regionEnabledBenefits
  }, [enabledBenefits, canRedeemLuxPlusBenefits])
}

export default useLandingBenefits
